"use client";

import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import * as React from "react";

import { renderAsChild } from "@/components/ui/render-as-child";
import { cn } from "@/utils/lib";
import { resolveClassName } from "@/utils/resolve-class-name";

const TooltipProvider = ({
  delay,
  delayDuration,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider> & {
  delayDuration?: number;
}) => <TooltipPrimitive.Provider delay={delay ?? delayDuration} {...props} />;
TooltipProvider.displayName = "TooltipProvider";

const Tooltip = ({
  delayDuration,
  disableHoverablePopup = true,
  disableHoverableContent,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root> & {
  disableHoverableContent?: boolean;
  delayDuration?: number;
}) => {
  void delayDuration;

  return (
    <TooltipPrimitive.Root
      disableHoverablePopup={disableHoverableContent ?? disableHoverablePopup}
      {...props}
    />
  );
};
Tooltip.displayName = "Tooltip";

const TooltipTrigger = ({
  asChild,
  children,
  render,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger> & {
  asChild?: boolean;
}) => (
  <TooltipPrimitive.Trigger
    render={render ?? renderAsChild(asChild, children)}
    {...props}
  >
    {asChild ? null : children}
  </TooltipPrimitive.Trigger>
);
TooltipTrigger.displayName = "TooltipTrigger";

const TooltipContent = ({
  className,
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  side = "top",
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Popup> &
  Pick<
    React.ComponentProps<typeof TooltipPrimitive.Positioner>,
    "align" | "alignOffset" | "side" | "sideOffset"
  >) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Positioner
      align={align}
      alignOffset={alignOffset}
      className="isolate z-100"
      side={side}
      sideOffset={sideOffset}
    >
      <TooltipPrimitive.Popup
        className={(state) =>
          cn(
            `
              z-100 w-fit max-w-xs origin-(--transform-origin) overflow-hidden rounded-md
              border bg-card px-3 py-1.5 text-xs/6 text-foreground shadow-lg
              shadow-accent outline-hidden transition duration-150
              ease-out data-ending-style:scale-95
              data-ending-style:opacity-0 data-starting-style:scale-95
              data-starting-style:opacity-0
            `,
            resolveClassName(className, state),
          )
        }
        {...props}
      />
    </TooltipPrimitive.Positioner>
  </TooltipPrimitive.Portal>
);
TooltipContent.displayName = "TooltipContent";

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
